import React, { useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { RxCross2 } from "react-icons/rx";
import { useLocation } from "react-router-dom";
import { VscSettings } from "react-icons/vsc";

interface SearchInputProps {
  onInputChange: (inputValue: string) => void;
  value: string;
  onClear: () => void;
  searchType?: string;
  onSearchTypeChange?: (searchType: string) => void;
}

const DEBOUNCE_DELAY = 1000;

const SearchInput: React.FC<SearchInputProps> = ({
  onInputChange,
  value,
  onClear,
  searchType = "*",
  onSearchTypeChange,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const [localValue, setLocalValue] = useState(value || "");
  const [localSearchType, setLocalSearchType] = useState(searchType);
  const [showDropdown, setShowDropdown] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const isChatAi = location.pathname === "/chatAI";
  const darkMode = localStorage.getItem("darkMode") === "true";

  const searchTypeOptions = [
    { value: "*", label: t("search.allFields", { defaultValue: "All fields" }) },
    { value: "title", label: t("search.title", { defaultValue: "Title" }) },
    { value: "text", label: t("search.text", { defaultValue: "Text" }) },
  ];

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  useEffect(() => {
    setLocalSearchType(searchType);
  }, [searchType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onInputChange(localValue);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const isValidValue = (val: string) => {
    if (val.length > 1000) {
      alert("Input is too long");
      return false;
    }
    return true;
  };

  const submitNow = () => {
    if (!isValidValue(localValue)) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onInputChange(localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") submitNow();
    if (e.key === "Escape") setShowDropdown(false);
  };

  const handleClear = () => {
    setLocalValue("");
    onClear();
  };

  return (
    <div className={`${isChatAi ? "w-[72%]" : "w-4/5"} relative`}>
      {/* ✅ Admin-style shell */}
      <div
        className={[
          "h-10 w-full",
          "flex items-center gap-2",
          "rounded-lg",
          "border border-slate-300 dark:border-slate-700",
          "bg-white dark:bg-slate-900",
          "px-3",
          "text-slate-900 dark:text-slate-100",
          "focus-within:ring-2 focus-within:ring-del-green/30",
        ].join(" ")}
      >
        <button
          type="button"
          onClick={submitNow}
          className="shrink-0 inline-flex items-center justify-center rounded-md"
          aria-label={t("search.submit", { defaultValue: "Search" })}
        >
          <FaSearch className="text-del-green" />
        </button>

        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={[
            "flex-1 min-w-0",
            "bg-transparent outline-none",
            "text-sm",
            "placeholder:text-slate-400 dark:placeholder:text-slate-500",
          ].join(" ")}
          placeholder={t("searchPlaceholder")}
        />

        {/* ✅ Small clear button (no huge X) */}
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            aria-label={t("search.clear", { defaultValue: "Clear search" })}
          >
            <RxCross2 className="text-slate-500 dark:text-slate-300" size={16} />
          </button>
        )}

        {/* ✅ Search-type dropdown (admin-like) */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowDropdown((s) => !s)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            aria-label={t("search.settings", { defaultValue: "Search settings" })}
          >
            <VscSettings className="text-slate-500 dark:text-slate-300" size={18} />
          </button>

          {showDropdown && (
            <div
              className={[
                "absolute right-0 mt-2",
                "min-w-[160px]",
                "rounded-lg",
                "border border-slate-300 dark:border-slate-700",
                "bg-white dark:bg-slate-900",
                "shadow-lg",
                "z-[9999]",
                "overflow-hidden",
                "text-sm",
                "font-normal",
              ].join(" ")}
            >
              {searchTypeOptions.map((option) => {
                const active = localSearchType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setLocalSearchType(option.value);
                      onSearchTypeChange?.(option.value);
                      setShowDropdown(false);
                    }}
                    className={[
                      "w-full text-left px-3 py-2 transition",
                      active
                        ? darkMode
                          ? "bg-slate-800 text-white"
                          : "bg-[#86BC25] text-black"
                        : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60",
                    ].join(" ")}
                  >
                    <span className="help-menu-item-text">{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SearchInput };